<script>
    import {onMount} from 'svelte';

    import {userStore} from '@js/stores/userStore';

    import {fetchRideCharts} from '@js/actions/rideActions';
    import {computeRideElevation} from '@js/actions/mapActions';

    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import Computation from '@js/modules/computation';

    import Notification from '@js/components/layouts/notification';

    import Spinner from '@js/components/themes/Spinner.svelte';
    import MapSlopeLegend from '@js/components/maps/MapSlopeLegend.svelte';

    import AnalyticsService from '@js/modules/analyticsService';

    const {
        map,
        currentRideType,
        rideId,
        rideShareUrl,
        serializedMapData,
        elevationData,
        hasDefaultClass = true,
        warningHelper = false,
        onElevationComputed,
        onHideElevation
    } = $props();

    const ELEVATION_TIMEOUT = 15000;

    let _charts = null;
    let _previousSerializedDataSize = serializedMapData?.length;
    let _previousElevationDataSize = elevationData?.length;
    let _loadTimeout = null;
    const _elevationRequestAbort = {};

    let isElevationDataLoaded = $state(false);
    let elevationError = $state(false);
    let isShowLoader = $state(false);

    let maximumAltitude = $state(null);
    let minimumAltitude = $state(null);
    let negativeElevation = $state(null);
    let positiveElevation = $state(null);

    const _showElevationChart = (chartData) => {
        import(/* webpackChunkName: "maps-chart" */ '@js/modules/maps/mapCharts')
            .then((chartModule) => {
                const Charts = chartModule.default;

                if (rideId) {
                    _charts = new Charts({
                        mode: Charts.mode.RIDE,
                        map,
                        rideId
                    });
                } else {
                    _charts = new Charts({
                        map,
                        rideId: 'new'
                    });
                }

                _charts.initializeCharts(chartData);
            });
    };

    const _computeMapElevation = () => {
        return computeRideElevation({
            mapPoints: serializedMapData,
            rideType: currentRideType
            // distance: mapStore.currentDistance
        }, {
            abort: _elevationRequestAbort
        })
            .then((chartData) => {
                if (!chartData) {
                    return;
                }

                if (chartData.errors) {
                    const errorMessage = chartData.errors;

                    AnalyticsService.trackElevationError(errorMessage);

                    Notification.error(errorMessage);

                    isShowLoader = false;
                    elevationError = true;

                    onHideElevation?.();
                } else if (chartData.chart) {
                    isElevationDataLoaded = true;

                    _showElevationChart(chartData.chart);

                    minimumAltitude = chartData.chart.minimumAltitude;
                    maximumAltitude = chartData.chart.maximumAltitude;
                    negativeElevation = chartData.chart.negativeElevation;
                    positiveElevation = chartData.chart.positiveElevation;

                    if (onElevationComputed) {
                        onElevationComputed(chartData.chart.points);
                    }
                }
            });
    };

    const _loadElevationData = () => {
        if (rideId) {
            fetchRideCharts(rideId, {share: rideShareUrl})
                .then((chartData) => {
                    if (!chartData) {
                        return;
                    }

                    isElevationDataLoaded = true;

                    _showElevationChart(chartData.chart);

                    if (onElevationComputed && chartData.chart?.points) {
                        onElevationComputed(chartData.chart.points);
                    }
                });
        } else if (elevationData) {
            isElevationDataLoaded = true;

            _showElevationChart($state.snapshot(elevationData));

            minimumAltitude = elevationData.minimumAltitude;
            maximumAltitude = elevationData.maximumAltitude;
            negativeElevation = elevationData.negativeElevation;
            positiveElevation = elevationData.positiveElevation;
        } else if (serializedMapData?.length) {
            _computeMapElevation()
                .then(() => {
                    setTimeout(() => Utils.scrollTo('elevation-chart-new', 300), 50);
                });
        }
    };

    onMount(() => {
        if (!isElevationDataLoaded) {
            _loadElevationData();
        }

        // Safety net: surface an error instead of spinning forever if the
        // elevation data never arrives (e.g. computation silently fails upstream).
        _loadTimeout = setTimeout(() => {
            if (!isElevationDataLoaded) {
                elevationError = true;
            }
        }, ELEVATION_TIMEOUT);

        return () => {
            if (_loadTimeout) {
                clearTimeout(_loadTimeout);
                _loadTimeout = null;
            }

            if (_charts) {
                _charts.removeCharts();
                _charts = null;
            }
        };
    });

    $effect(() => {
        if (isElevationDataLoaded && _loadTimeout) {
            clearTimeout(_loadTimeout);
            _loadTimeout = null;
        }
    });

    $effect(() => {
        if (serializedMapData && _previousSerializedDataSize !== serializedMapData.length) {
            _previousSerializedDataSize = serializedMapData.length;

            isShowLoader = true;
            elevationError = false;
            maximumAltitude = null;
            minimumAltitude = null;
            negativeElevation = null;
            positiveElevation = null;

            if (_elevationRequestAbort?.signal) {
                _elevationRequestAbort.signal.abort();
            }

            if (_charts) {
                _charts.removeCharts();
            }

            _computeMapElevation()
                .then(() => {
                    isShowLoader = false;
                });
        }
    });

    $effect(() => {
        if (elevationData?.points && _previousElevationDataSize !== elevationData.points.length) {
            _previousElevationDataSize = elevationData.points.length;

            isShowLoader = true;
            elevationError = false;
            maximumAltitude = null;
            minimumAltitude = null;
            negativeElevation = null;
            positiveElevation = null;

            if (_charts) {
                _charts.removeCharts();
            }

            isElevationDataLoaded = true;

            _showElevationChart($state.snapshot(elevationData));

            minimumAltitude = elevationData.minimumAltitude;
            maximumAltitude = elevationData.maximumAltitude;
            negativeElevation = elevationData.negativeElevation;
            positiveElevation = elevationData.positiveElevation;
            isShowLoader = false;
        }
    });
</script>

{#snippet elevationFigure(figure, name)}
    <div class="flex items-center justify-center px-2 sm:px-4">
        <div class="flex flex-col items-center justify-between">
            <div class="text-xl font-bold text-gray-900 whitespace-nowrap dark:text-white">
                {Computation.formatDistance(figure, {
                    unit: $userStore.unit,
                    smartUnit: false,
                    kUnit: false
                })}
            </div>

            <div class="text-xs text-center text-gray-500 sm:whitespace-nowrap dark:text-gray-400">
                {name}
            </div>
        </div>
    </div>
{/snippet}

<div class="mt-1"
     class:map-elevation-graph={hasDefaultClass}>
    {#if !isElevationDataLoaded && !elevationError}
        <div class="text-center">
            <div class="mt-2">
                {I18n.t('js.ride.elevation.computation')}
            </div>

            <div class="mt-4">
                <Spinner class="me-3"
                         size="8"/>
            </div>
        </div>
    {/if}

    {#if elevationError}
        <div class="mt-2 text-center text-sm text-red-600">
            {I18n.t('js.ride.elevation.error')}
        </div>
    {/if}

    <div id={`elevation-chart-${rideId || 'new'}`}
         class="text-center"></div>

    {#if isElevationDataLoaded && !elevationError}
        <MapSlopeLegend class="map-slope-legend--inline"/>
    {/if}

    {#if isShowLoader}
        <div class="mt-4 text-center">
            <Spinner class="me-3"
                     size="8"/>
        </div>
    {/if}

    {#if warningHelper}
        <div class="p-2 text-2xs leading-3 text-center text-gray-400 italic">
            {I18n.t('js.ride.show.elevation_info')}
        </div>
    {/if}

    <div class="grid grid-cols-2 gap-y-3 p-4 sm:flex sm:flex-row sm:items-center sm:justify-center">
        {#if positiveElevation}
            {@render elevationFigure(positiveElevation, I18n.t('js.ride.model.elevation_positive'))}
        {/if}
        {#if negativeElevation}
            {@render elevationFigure(negativeElevation, I18n.t('js.ride.model.elevation_negative'))}
        {/if}
        {#if minimumAltitude}
            {@render elevationFigure(minimumAltitude, I18n.t('js.ride.model.minimum_altitude'))}
        {/if}
        {#if maximumAltitude}
            {@render elevationFigure(maximumAltitude, I18n.t('js.ride.model.maximum_altitude'))}
        {/if}
    </div>
</div>

<style>
    .map-elevation-graph {
        padding: .8rem .8rem 0;
        border-bottom-left-radius: 1rem;
        border-bottom-right-radius: 1rem;
        box-shadow: rgba(0, 0, 0, 0.16) 0 3px 6px, rgba(0, 0, 0, 0.23) 0 3px 6px;
    }
</style>
